import { type VariantProps, cva } from "class-variance-authority";
import { ArrowRight, FileText } from "lucide-react";
import * as React from "react";
import { Badge, type BadgeVariant } from "../atoms/badge.js";
import { Button } from "../atoms/button.js";
import { cx } from "../internal/cx.js";

const billingOverviewRecentPaymentCardVariants = cva("cw-v2-billing-overview-recent-payment-card", {
  variants: {
    variant: {
      default: "cw-v2-billing-overview-recent-payment-card--default",
    },
    size: {
      md: "cw-v2-billing-overview-recent-payment-card--md",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "md",
  },
});

export interface BillingOverviewRecentPaymentCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof billingOverviewRecentPaymentCardVariants> {
  /** Card title. Default "Recent payment". */
  title?: React.ReactNode;
  /** Coverage period covered by this payment. */
  coveragePeriod?: React.ReactNode;
  /** Already-formatted payment amount. */
  amount?: React.ReactNode;
  /** Payment status label. */
  status?: React.ReactNode;
  /** Optional explicit badge tone. Inferred from status text when omitted. */
  statusTone?: BadgeVariant;
  /** Draft date or payment date copy. */
  draftDate?: React.ReactNode;
  /** Already-formatted payment method line. */
  paymentMethod?: React.ReactNode;
  /** Covered employee count/value. */
  coveredEmployees?: React.ReactNode;
  /** Optional note below the card. */
  note?: React.ReactNode;
  /** Custom benefits statement action. Overrides `onViewStatement`. */
  statementAction?: React.ReactNode;
  /** Benefits statement button label. */
  viewStatementLabel?: React.ReactNode;
  /** Billing history link-style button label. */
  viewHistoryLabel?: React.ReactNode;
  /** Fires when the benefits statement button is clicked. */
  onViewStatement?: () => void;
  /** Fires when the billing history link is clicked. */
  onViewHistory?: () => void;
}

/**
 * Recent payment summary for Billing Overview. Keeps the original
 * top-row/bottom-row statement layout while using v2 card, badge, and button
 * treatments.
 */
export const BillingOverviewRecentPaymentCard = React.forwardRef<
  HTMLDivElement,
  BillingOverviewRecentPaymentCardProps
>(function BillingOverviewRecentPaymentCard(
  {
    amount,
    className,
    coveragePeriod,
    coveredEmployees,
    draftDate,
    note,
    onViewHistory,
    onViewStatement,
    paymentMethod,
    size,
    statementAction,
    status,
    statusTone,
    title = "Recent payment",
    variant,
    viewHistoryLabel = "See all billing history",
    viewStatementLabel = "See benefits statement",
    ...props
  },
  ref,
) {
  const resolvedStatusTone = statusTone ?? getStatusTone(status);

  return (
    <div
      ref={ref}
      className={cx(billingOverviewRecentPaymentCardVariants({ variant, size }), className)}
      {...props}
    >
      <h3 className="cw-v2-billing-overview-recent-payment-card__title">{title}</h3>
      <div className="cw-v2-billing-overview-recent-payment-card__panel">
        <div className="cw-v2-billing-overview-recent-payment-card__top">
          <RecentPaymentField
            className="cw-v2-billing-overview-recent-payment-card__coverage"
            label="Coverage Period"
            value={coveragePeriod}
            valueSize="lg"
          />
          <div className="cw-v2-billing-overview-recent-payment-card__amount-status">
            <RecentPaymentField label="Amount" value={amount} valueSize="lg" />
            {status ? (
              <div className="cw-v2-billing-overview-recent-payment-card__field cw-v2-billing-overview-recent-payment-card__status">
                <div className="cw-v2-billing-overview-recent-payment-card__label">Status</div>
                <Badge size="sm" variant={resolvedStatusTone}>
                  {status}
                </Badge>
              </div>
            ) : null}
          </div>
        </div>

        <div className="cw-v2-billing-overview-recent-payment-card__bottom">
          <div className="cw-v2-billing-overview-recent-payment-card__details">
            <RecentPaymentField label="Draft Date" value={draftDate} />
            <RecentPaymentField label="Payment method" value={paymentMethod} />
            {coveredEmployees ? (
              <RecentPaymentField label="Covered employees" value={coveredEmployees} />
            ) : null}
          </div>
          {statementAction ??
            (onViewStatement ? (
              <Button
                className="cw-v2-billing-overview-recent-payment-card__statement"
                iconPrefix={<FileText aria-hidden size={16} />}
                onClick={onViewStatement}
                size="sm"
                variant="outline"
              >
                {viewStatementLabel}
              </Button>
            ) : null)}
        </div>

        {onViewHistory ? (
          <Button
            className="cw-v2-billing-overview-recent-payment-card__history"
            iconSuffix={<ArrowRight aria-hidden size={16} />}
            onClick={onViewHistory}
            size="sm"
            variant="link"
          >
            {viewHistoryLabel}
          </Button>
        ) : null}
      </div>
      {note ? <p className="cw-v2-billing-overview-recent-payment-card__note">{note}</p> : null}
    </div>
  );
});
BillingOverviewRecentPaymentCard.displayName = "BillingOverviewRecentPaymentCard";

function getStatusTone(status: React.ReactNode): BadgeVariant {
  if (typeof status !== "string") return "neutral";
  const normalized = status.toLowerCase();
  if (normalized.includes("paid") || normalized.includes("completed")) return "success";
  if (
    normalized.includes("processing") ||
    normalized.includes("pending") ||
    normalized.includes("scheduled")
  ) {
    return "warning";
  }
  if (normalized.includes("fail") || normalized.includes("declin")) return "danger";
  return "neutral";
}

interface RecentPaymentFieldProps {
  className?: string;
  label: React.ReactNode;
  value?: React.ReactNode;
  valueSize?: "md" | "lg";
}

function RecentPaymentField({
  className,
  label,
  value,
  valueSize = "md",
}: RecentPaymentFieldProps) {
  if (!value) return null;

  return (
    <div className={cx("cw-v2-billing-overview-recent-payment-card__field", className)}>
      <div className="cw-v2-billing-overview-recent-payment-card__label">{label}</div>
      <div
        className={cx(
          "cw-v2-billing-overview-recent-payment-card__value",
          valueSize === "lg" && "cw-v2-billing-overview-recent-payment-card__value--lg",
        )}
      >
        {value}
      </div>
    </div>
  );
}

export const V2BillingOverviewRecentPaymentCard = BillingOverviewRecentPaymentCard;
export type V2BillingOverviewRecentPaymentCardProps = BillingOverviewRecentPaymentCardProps;
